import { Link } from 'react-router-dom';
import { enums, getFormattedHoursOfOperation } from '../../utils';
import chatCircle from '../../images/chat-circle.svg';
import phoneCircle from '../../images/phone-circle.svg';
import phoneScheduleACall from '../../images/phone-schedule-a-call-circle.svg';
import { KenticoDataContext } from '../../contexts/kentico-data-context';
import { PersonDataContext } from '../../contexts/person-data-context/index';

import React from 'react';

type Props = {
  children: React.ReactNode;
  className?: string;
};

type ContainerProps = {
  children: React.ReactNode;
  id?: string;
  className?: string;
};

type LinkProps = {
  to: string;
  children: React.ReactNode;
  className?: string;
};

export const Container = ({ children, id, className = '' }: ContainerProps) => {
  return (
    <div id={id} className={`tw-bg-inherit ${className}`}>
      {children}
    </div>
  );
};

export const Heading = ({ children, className = '' }: Props) => {
  return (
    <h2
      className={`tw-text-unm-adhoc2 tw-font-ubuntu tw-font-light tw-text-[46px] tw-ml-4 ${className}`}
    >
      {children}
    </h2>
  );
};

export const SubHeading = ({ children, className = '' }: Props) => {
  return (
    <h5
      className={`tw-text-unm-adhoc2 tw-font-ubuntu tw-font-light tw-mt-5 tw-mr-5 tw-mb-6 tw-ml-4 ${className}`}
    >
      {children}
    </h5>
  );
};

export const LiveChatCard = ({
  children,
  className = '',
  ...props
}: Props & React.ComponentProps<'button'>) => {
  return (
    <button
      {...props}
      className={`tw-m-4 tw-bg-white tw-rounded-lg tw-shadow-basic tw-border-none tw-p-5 tw-w-full sm:tw-w-[280px] sm:tw-h-[194px] ${className}`}
    >
      {children}
    </button>
  );
};

export const LinkCard = ({
  to,
  children,
  className = '',
  ...props
}: LinkProps) => {
  return (
    <Link
      to={to}
      {...props}
      className={`tw-flex tw-flex-col tw-p-5 tw-m-4 tw-bg-white tw-rounded-lg tw-shadow-basic tw-w-full sm:tw-w-[280px] sm:tw-h-[194px] tw-no-underline hover:tw-no-underline ${className}`}
    >
      {children}
    </Link>
  );
};

export const Card = ({ children, className = '', ...props }: Props) => {
  return (
    <div
      {...props}
      className={`tw-flex tw-flex-col tw-p-5 tw-m-4 tw-bg-white tw-rounded-lg tw-shadow-basic tw-w-full sm:tw-w-[280px] sm:tw-h-[194px] tw-no-underline hover:tw-no-underline ${className}`}
    >
      {children}
    </div>
  );
};

export const CardHeader = ({ children, className = '' }: Props) => {
  return (
    <div
      className={`tw-text-unm-interactive01-800 tw-font-ubuntu tw-font-light tw-text-xl tw-text-left ${className}`}
    >
      {children}
    </div>
  );
};

export const CardBody = ({ children, className = '' }: Props) => {
  return (
    <div
      className={`tw-text-unm-secondary01-700 tw-font-sans tw-text-base tw-text-left tw-h-full tw-mt-4 ${className}`}
    >
      {children}
    </div>
  );
};

const ContactUs: (p: any) => React.ReactElement = (_p) => {
  const { callCenter, config } = React.useContext(KenticoDataContext);
  const { personData } = React.useContext(PersonDataContext);
  const employerId = personData?.person?.companyCode;

  const clientIdentifierList: string[] =
    config['phoenix_client_identifier_list']?.split(',') || [];
  const unumIdentifier: string | undefined = clientIdentifierList
    .find((item: string) => item.startsWith('Unum::'))
    ?.split('::')[1];

  const { hoost, hooet } = getFormattedHoursOfOperation(callCenter);

  const liveChatStatusClass = `live-chat-${
    callCenter.open ? 'online' : 'offline'
  }`;

  return (
    <Container id="contact-us-container">
      <Heading>Contact Us</Heading>
      <SubHeading>
        We’re happy to answer your questions. Select an option below to connect
        to one of our specialists.
      </SubHeading>

      <div className="tw-flex tw-flex-wrap">
        <LiveChatCard
          id="live-chat-card"
          onClick={() => {
            if (callCenter.open) {
              (window as any).openLiveChat();
            }
          }}
          className="tw-cursor-pointer"
        >
          <CardHeader className="tw-flex tw-items-center">
            <img src={chatCircle} className="tw-mr-2" alt="live chat"></img>
            <div
              className={`tw-flex tw-items-center tw-justify-between tw-gap-4 ${liveChatStatusClass}`}
            >
              Live chat
            </div>
          </CardHeader>
          <CardBody>
            <>
              Chat online, in real-time, with one of our representatives
              <br />
              <b>
                Monday through Friday,
                <br /> {hoost} - {hooet}, ET.
              </b>
            </>
          </CardBody>
        </LiveChatCard>

        <LinkCard to={enums.AppPaths.contactUsCallback}>
          <CardHeader className="tw-flex tw-items-center">
            <img
              src={phoneScheduleACall}
              className="tw-mr-2"
              alt="schedule a callback"
            />
            <span>Schedule a call</span>
          </CardHeader>
          <CardBody>
            Schedule a call to speak with one of our representatives,
            <br />
            <b>
              Monday through Friday,
              <br /> {hoost} - {hooet}, ET.
            </b>
          </CardBody>
        </LinkCard>

        {employerId !== unumIdentifier && (
          <Card>
            <CardHeader className="tw-flex tw-items-center">
              <img src={phoneCircle} className="tw-mr-2" alt="call us" />
              <span>Call us</span>
            </CardHeader>
            <CardBody>
              Dial{' '}
              <a
                href="tel:+8668686737"
                className="tw-text-unm-interactive01-400 hover:tw-text-unm-interactive01-700 tw-underline"
              >
                866-868-6737
              </a>{' '}
              to speak with one of our representatives.
              <br />
              <b>
                Monday through Friday,
                <br /> {hoost} - {hooet}, ET.
              </b>
            </CardBody>
          </Card>
        )}
      </div>
    </Container>
  );
};

export default ContactUs;
